import { BdExportAsFileConverter } from '../export.converter';
import { unparse, UnparseConfig } from 'papaparse';
import {
  BD_NUMBER_FORMATTER_OPTIONS_DEFAULT,
  BdExportAsFileDataRow,
  BdExportAsFileDataRowItem, BdExportAsFileDataSheet,
  BdNumberFormatter,
} from '@bindoc-projects/util-domain-lib';
import NumberFormatOptions = Intl.NumberFormatOptions;
import DateTimeFormatOptions = Intl.DateTimeFormatOptions;

export class BdExportAsFileCsvConverter extends BdExportAsFileConverter {
  constructor(
    private _exportData: Promise<BdExportAsFileDataSheet>,
    locale: string,
    filenameWithoutSuffix: string
  ) {
    super(locale, `${filenameWithoutSuffix}.csv`);
  }

  private readonly _defaultConfig: UnparseConfig = {
    quotes: false,
    quoteChar: '"',
    delimiter: ';',
    header: true,
    newline: '\r\n',
  };

  async process(): Promise<{ result?: Blob; errors?: string[] }> {
    const processed: { result?: Blob; errors?: string[] } = {};
    const exportDataResponse = await this._exportData;

    try {
      const csvFile = unparse<string[]>(
        {
          data: this._getData(exportDataResponse.data),
          fields: exportDataResponse.fields,
        },
        this._defaultConfig
      );
      processed.result = new Blob(['\uFEFF' + csvFile], {
        type: 'text/csv;charset=utf-8',
      });
    } catch (e: unknown) {
      processed.errors = [e instanceof Error ? e.message : 'Unknown error'];
    }

    return Promise.resolve(processed);
  }

  private _getData(rows: BdExportAsFileDataRow[]): string[][] {
    return rows.map((row) => row.map((item) => this._itemToString(item)));
  }

  private _itemToString(item: BdExportAsFileDataRowItem): string {
    if (!item.value) {
      return '';
    }

    if (item.type === 'boolean') {
      return (item.value as boolean) ? 'ja' : 'nein';
    }

    if (item.type === 'number') {
      const options = BdExportAsFileCsvConverter._instanceOfNumberFormatOptions(
        item.format
      )
        ? (item.format as NumberFormatOptions)
        : BD_NUMBER_FORMATTER_OPTIONS_DEFAULT;

      return BdNumberFormatter.format(item.value as number, options, [
        this._locale,
      ]);
    }

    if (item.type === 'Date') {
      const options =
        BdExportAsFileCsvConverter._instanceOfDateTimeFormatOptions(item.format)
          ? (item.format as DateTimeFormatOptions)
          : undefined;

      return Intl.DateTimeFormat(this._locale, options).format(
        item.value as Date
      );
    }

    return item.value.toString();
  }
}
